/**
 * TourismDivergenceChart — "The demand came back. The economics didn't."
 * Visitor arrivals (HKTB/Tourism Commission) vs restaurant receipts VOLUME
 * (chained from C&SD annual volume % changes), both indexed to 2018 = 100.
 * Lines animate on load; dots carry hover/tap tooltips with the raw figures.
 * Requires React 16.8+. No props; injects scoped styles + Poppins once.
 */
'use client';

import React, { useEffect } from 'react';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Data --------------------------------- */
const YEARS: number[] = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];
const ARR_IX: number[] = [100, 85.8, 5.5, 0.1, 0.9, 52.2, 68.3, 76.6];
const VOL_IX: number[] = [100, 92.0, 64.4, 73.9, 66.9, 81.4, 79.4, 78.7];
const ARR_LBL: string[] = ['65.1M', '55.9M', '3.6M', '91K', '0.6M', '34.0M', '44.5M', '49.9M'];
const RCP_B: number[] = [119.5, 112.5, 79.4, 92.7, 86.8, 109.5, 109.4, 109.6];
const VOL_CHG: string[] = ['+3.1%', '−8.0%', '−30.0%', '+14.8%', '−9.5%', '+21.6%', '−2.4%', '−0.9%'];

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'tdc-styles';
const CSS = `
.tdc-root { --tdc-font: 'Poppins', system-ui, -apple-system, sans-serif; --tdc-mono: var(--tdc-font);
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --line-2: #EEF1F2; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --gold: #B08A2E; --gold-deep: #7A5F1D;
  position: relative; box-sizing: border-box; width: 100%; max-width: 980px;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 28px; color: var(--ink);
  font-family: var(--tdc-font); -webkit-font-smoothing: antialiased; }
.tdc-root .tdc-logo { position: absolute; top: 40px; right: 44px; height: 40px; width: auto; }
.tdc-root.compact .tdc-logo { display: none; }
.tdc-root *, .tdc-root *::before, .tdc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.tdc-root h1, .tdc-root h2, .tdc-root h3, .tdc-root p, .tdc-root img { margin: 0; }
.tdc-root p { letter-spacing: normal; }
.tdc-root .head { display: flex; justify-content: space-between; align-items: flex-end; gap: 32px; flex-wrap: wrap; }
.tdc-root h1 { font-size: 30px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.25; max-width: 560px; }
.tdc-root h1 .gold { color: var(--gold); }
.tdc-root .sub { font-size: 14px; color: var(--body); margin-top: 10px; max-width: 560px; line-height: 1.5; }
.tdc-root .legend { display: flex; flex-direction: column; gap: 8px; flex: none; padding: 14px 18px; border: 1px solid var(--line); border-radius: 10px; background: var(--wash); }
.tdc-root .legend .li { display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 500; color: var(--ink-2); }
.tdc-root .legend svg { flex: none; }
.tdc-root .chart-wrap { margin-top: 26px; position: relative; }
.tdc-root svg.plot { display: block; width: 100%; height: auto; overflow: visible; }
.tdc-root .grid line { stroke: var(--line-2); stroke-width: 1; }
.tdc-root .axis-lbl { font-family: var(--tdc-mono); font-size: 12.5px; fill: var(--muted); }
.tdc-root .base-lbl { font-family: var(--tdc-mono); font-size: 11px; font-weight: 600; letter-spacing: 0.08em; fill: var(--muted); text-transform: uppercase; }
.tdc-root .band-lbl { font-family: var(--tdc-mono); font-size: 10.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; fill: var(--ink-2); }
.tdc-root .end-val { font-size: 21px; font-weight: 600; }
.tdc-root .end-sub { font-size: 11.5px; font-weight: 500; fill: var(--muted); }
.tdc-root .anno { font-size: 14px; font-weight: 600; fill: var(--gold-deep); }
.tdc-root .anno-sub { font-size: 13px; font-weight: 500; fill: var(--gold); }
.tdc-root .halo { paint-order: stroke; stroke: #FFFFFF; stroke-width: 4px; stroke-linejoin: round; }
.tdc-root .dot { transition: opacity .3s ease; }
.tdc-root .hit { cursor: pointer; }
.tdc-root .fade { transition: opacity .5s ease; }
.tdc-root .tip { position: absolute; pointer-events: none; background: var(--ink); color: #fff; padding: 9px 12px; border-radius: 10px; font-size: 12.5px; line-height: 1.5; opacity: 0; transform: translate(-50%, calc(-100% - 14px)); transition: opacity .15s; white-space: nowrap; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5); z-index: 5; }
.tdc-root .tip b { font-weight: 600; }
.tdc-root .tip .t-val { color: #A9D9D4; font-family: var(--tdc-mono); }
.tdc-root .tip::after { content: ''; position: absolute; left: 50%; bottom: -5px; transform: translateX(-50%) rotate(45deg); width: 10px; height: 10px; background: var(--ink); }
.tdc-root .foot { margin-top: 22px; padding-top: 16px; border-top: 1px solid var(--line); display: flex; justify-content: space-between; gap: 24px; flex-wrap: wrap; }
.tdc-root .foot .cap { font-size: 12.5px; line-height: 1.55; color: var(--body); font-style: italic; max-width: 660px; }
.tdc-root .foot .brand { font-family: var(--tdc-mono); font-size: 10px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); white-space: nowrap; }
.tdc-root .src { margin-top: 12px; font-family: var(--tdc-mono); font-size: 10px; color: var(--muted); line-height: 1.6; }
.tdc-root svg.mob .axis-lbl { font-size: 17px; }
.tdc-root svg.mob .base-lbl { font-size: 14px; }
.tdc-root svg.mob .band-lbl { font-size: 13px; }
.tdc-root svg.mob .end-val { font-size: 28px; }
.tdc-root svg.mob .end-sub { font-size: 15px; }
.tdc-root svg.mob .anno { font-size: 18.5px; }
.tdc-root svg.mob .anno-sub { font-size: 17px; }
.tdc-root.compact { padding: 26px 0 20px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.tdc-root.compact h1 { font-size: 22px; }
.tdc-root.compact .sub { font-size: 13px; }
.tdc-root.compact .head { gap: 16px; }
.tdc-root.compact .legend { flex-direction: row; flex-wrap: wrap; gap: 8px 18px; width: 100%; padding: 12px 14px; }
.tdc-root.compact .legend .li { font-size: 12px; }
.tdc-root.compact .chart-wrap { margin-top: 20px; }
.tdc-root.compact .foot { flex-direction: column; gap: 10px; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

function useInView(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [inView, setInView] = React.useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

/* ------------------------------ Component ------------------------------ */
interface Tip { html: string; x: number; y: number; }

const TourismDivergenceChart: React.FC = () => {
  useInjectedStyles();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef);
  const [compact, setCompact] = React.useState(false);
  const [drawn, setDrawn] = React.useState(false);
  // `tip` keeps the last tooltip's position/content so the fade-out happens in
  // place; clearing it on leave would jump the fading box to the corner.
  const [tip, setTip] = React.useState<Tip | null>(null);
  const [tipOpen, setTipOpen] = React.useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = (): void => setCompact(el.offsetWidth < 640);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);

  // Lines draw in when the chart scrolls into view; dots/annotations follow.
  useEffect(() => {
    if (!inView) return;
    const t = window.setTimeout(() => setDrawn(true), 750);
    return () => window.clearTimeout(t);
  }, [inView]);

  // pathLength=1 normalizes the path, so dasharray/dashoffset of 1 hide/draw it.
  const lineDraw = (delay: number): React.CSSProperties => ({
    strokeDasharray: 1,
    strokeDashoffset: inView ? 0 : 1,
    transition: `stroke-dashoffset 1.2s ${delay}s cubic-bezier(0.19,1,0.22,1)`,
  });

  const mob = compact;
  const W = mob ? 540 : 920, H = mob ? 520 : 440;
  const M = mob ? { top: 46, right: 128, bottom: 52, left: 44 } : { top: 40, right: 148, bottom: 46, left: 48 };
  const plotW = W - M.left - M.right, plotH = H - M.top - M.bottom;
  const MAX = 106;
  const x = (i: number): number => M.left + (i / (YEARS.length - 1)) * plotW;
  const y = (v: number): number => M.top + plotH * (1 - v / MAX);
  const path = (ix: number[]): string => ix.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const last = YEARS.length - 1;
  const bandX0 = x(1.55), bandX1 = x(4.65);

  const arrTip = (i: number): Tip => ({
    x: x(i), y: y(ARR_IX[i]),
    html: `<b>${YEARS[i]} · Arrivals</b><br>${ARR_LBL[i]} visitors · <span class="t-val">index ${ARR_IX[i].toFixed(1)}</span>`,
  });
  const volTip = (i: number): Tip => ({
    x: x(i), y: y(VOL_IX[i]),
    html: `<b>${YEARS[i]} · Dining volume</b><br>HK$${RCP_B[i].toFixed(1)}B · vol ${VOL_CHG[i]} · <span class="t-val">index ${VOL_IX[i].toFixed(1)}</span>`,
  });

  const show = (t: Tip): void => { setTip(t); setTipOpen(true); };

  const fade = (delay: number): React.CSSProperties => ({ opacity: drawn ? 1 : 0, transitionDelay: `${delay}ms` });

  return (
    <div className={`tdc-root${compact ? ' compact' : ''}`} ref={rootRef}>
      <img className="tdc-logo" src={PSGLOBAL_LOGO} alt="PS Global Consulting" />
      <div className="head">
        <div>
          <h1>The demand came back. <span className="gold">The economics didn&rsquo;t.</span></h1>
          <p className="sub">Visitor arrivals vs real dining volume, indexed to 2018 = 100. Arrivals have climbed every year since reopening — dining volume peaked in the 2023 reopening year and has fallen two years running.</p>
        </div>
        <div className="legend">
          <div className="li"><svg width="26" height="12"><line x1="0" y1="6" x2="26" y2="6" stroke="#0f9993" strokeWidth="2.75" /></svg>Visitor arrivals</div>
          <div className="li"><svg width="26" height="12"><line x1="0" y1="6" x2="26" y2="6" stroke="#3E5460" strokeWidth="2.25" /></svg>Dining volume</div>
        </div>
      </div>

      <div className="chart-wrap">
        <svg className={`plot${mob ? ' mob' : ''}`} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Visitor arrivals recover toward pre-Covid levels while restaurant volume peaks in 2023 and declines, 2018 to 2025">
          <rect x={bandX0} y={y(100)} width={bandX1 - bandX0} height={y(0) - y(100)} fill="#F5F8F8" />
          <text className="band-lbl" x={(bandX0 + bandX1) / 2} y={y(100) + 20} textAnchor="middle">Travel restrictions</text>
          <g className="grid">
            {[0, 50, 100].map((v) => (
              <g key={v}>
                <line x1={M.left} y1={y(v)} x2={W - M.right} y2={y(v)} />
                <text className="axis-lbl" x={M.left - 10} y={y(v) + 5} textAnchor="end">{v}</text>
              </g>
            ))}
          </g>
          {YEARS.map((yr, i) => (
            <text key={yr} className="axis-lbl" x={x(i)} y={H - M.bottom + (mob ? 30 : 26)} textAnchor="middle">{mob ? `’${String(yr).slice(2)}` : yr}</text>
          ))}
          <path pathLength={1} d={path(VOL_IX)} fill="none" stroke="#3E5460" strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round" style={lineDraw(0)} />
          <path pathLength={1} d={path(ARR_IX)} fill="none" stroke="#0f9993" strokeWidth="2.75" strokeLinejoin="round" strokeLinecap="round" style={lineDraw(0.2)} />
          {VOL_IX.map((v, i) => (
            <g key={`r${i}`}>
              <circle className="dot" style={fade(i * 50)} cx={x(i)} cy={y(v)} r={mob ? 4 : 3.25} fill="#fff" stroke="#3E5460" strokeWidth="2" />
              <circle className="hit" cx={x(i)} cy={y(v)} r="14" fill="transparent" onMouseEnter={() => show(volTip(i))} onMouseLeave={() => setTipOpen(false)} onClick={() => show(volTip(i))} />
            </g>
          ))}
          {ARR_IX.map((v, i) => (
            <g key={`a${i}`}>
              <circle className="dot" style={fade(i * 50)} cx={x(i)} cy={y(v)} r={mob ? 4.5 : 3.5} fill="#fff" stroke="#0f9993" strokeWidth="2.25" />
              <circle className="hit" cx={x(i)} cy={y(v)} r="14" fill="transparent" onMouseEnter={() => show(arrTip(i))} onMouseLeave={() => setTipOpen(false)} onClick={() => show(arrTip(i))} />
            </g>
          ))}
          <g className="fade" style={fade(350)}>
            <text className="anno halo" x={x(6)} y={y(38)} textAnchor="middle">Since 2023</text>
            <text className="anno-sub halo" x={x(6)} y={y(38) + (mob ? 24 : 20)} textAnchor="middle">arrivals +47% · dining −3%</text>
          </g>
          <g className="fade" style={fade(250)}>
            <text className="end-sub" x={x(last) + 14} y={y(VOL_IX[last]) - (mob ? 56 : 46)}>Dining volume</text>
            <text className="end-val" x={x(last) + 14} y={y(VOL_IX[last]) - (mob ? 28 : 22)} fill="#3E5460">79</text>
            <text className="end-val" x={x(last) + 14} y={y(ARR_IX[last]) + (mob ? 32 : 26)} fill="#0d555c">77</text>
            <text className="end-sub" x={x(last) + 14} y={y(ARR_IX[last]) + (mob ? 52 : 44)}>Arrivals · 49.9M</text>
          </g>
        </svg>
        <div className="tip" style={tip ? { left: `${(tip.x / W) * 100}%`, top: `${(tip.y / H) * 100}%`, opacity: tipOpen ? 1 : 0 } : undefined} dangerouslySetInnerHTML={{ __html: tip ? tip.html : '' }}></div>
      </div>

      <div className="foot">
        <p className="cap">Arrivals recovered from 34M in 2023 to 49.9M in 2025. Real dining volume went the other way: 81.4 in 2023, 79.4 in 2024, 78.7 in 2025. The visitors returned; the dining didn&rsquo;t follow.</p>
      </div>
      <p className="src">Sources: HKTB / Tourism Commission visitor arrivals · C&amp;SD Quarterly Survey of Restaurant Receipts and Purchases (provisional annual estimates). Volume index chained from C&amp;SD annual volume % changes, 2018 = 100.</p>
    </div>
  );
};

export default TourismDivergenceChart;
